import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';

const ITYPES = ['afrekening', 'tussentijds', 'voorschot', 'jaarafrekening'];

// Velden die zowel bij elek als gas relevant zijn / specifiek zijn
function num(v) { return v === '' || v == null ? '' : v; }

/**
 * Herbruikbaar factuurformulier.
 * props:
 *   connections : lijst aansluitingen
 *   initial     : voor-ingevulde waarden (van parser of bestaande factuur)
 *   invoiceId   : indien gezet → PUT (bewerken), anders POST (nieuw)
 *   meta        : { source, parse_status, pdf_filename, raw_json } — meegestuurd bij opslaan
 */
export default function InvoiceForm({ connections, initial = {}, invoiceId = null, meta = {}, onSaved = null }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    connection_id:     initial.connection_id     || (connections[0]?.id ?? ''),
    invoice_number:    initial.invoice_number    || '',
    invoice_type:      initial.invoice_type      || 'afrekening',
    invoice_date:      (initial.invoice_date     || '').slice(0, 10),
    period_start:      (initial.period_start      || '').slice(0, 10),
    period_end:        (initial.period_end        || '').slice(0, 10),
    consumption_kwh:   num(initial.consumption_kwh),
    conversion_factor: num(initial.conversion_factor),
    consumption_total: num(initial.consumption_total),
    consumption_day:   num(initial.consumption_day),
    consumption_night: num(initial.consumption_night),
    injection_kwh:     num(initial.injection_kwh),
    peak_power_kw:     num(initial.peak_power_kw),
    amount_energy:     num(initial.amount_energy),
    amount_network:    num(initial.amount_network),
    amount_taxes:      num(initial.amount_taxes),
    amount_excl_vat:   num(initial.amount_excl_vat),
    vat_amount:        num(initial.vat_amount),
    amount_incl_vat:   num(initial.amount_incl_vat),
    notes:             initial.notes || '',
  });

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  const selectedConn = connections.find(c => String(c.id) === String(form.connection_id));
  const isElec = selectedConn?.energy_type === 'elektriciteit';

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const payload = {
      ...form,
      // Bij bewerken de bestaande herkomst/status behouden; bij nieuw 'manual'
      source:       meta.source       || initial.source       || 'manual',
      parse_status: meta.parse_status || initial.parse_status || 'manual',
      pdf_filename: meta.pdf_filename  || initial.pdf_filename || null,
      raw_json:     meta.raw_json,
    };
    try {
      if (invoiceId) await api.put(`/api/invoices/${invoiceId}`, payload);
      else           await api.post('/api/invoices', payload);
      if (onSaved) onSaved();
      else         navigate('/invoices');
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  const numField = (key, label, extra = {}) => (
    <div className="col-md-4 mb-3">
      <label className="form-label small fw-medium">{label}</label>
      <input type="number" step="any" className="form-control" value={form[key]}
        onChange={e => set(key, e.target.value)} {...extra} />
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-0">
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_connection')} *</label>
              <select className="form-select" value={form.connection_id}
                onChange={e => set('connection_id', e.target.value)} required>
                {connections.map(c => (
                  <option key={c.id} value={c.id}>{c.label} ({t(`type_${c.energy_type}`)})</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_number')}</label>
              <input className="form-control" value={form.invoice_number}
                onChange={e => set('invoice_number', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label small fw-medium">{t('inv_type')}</label>
              <select className="form-select" value={form.invoice_type}
                onChange={e => set('invoice_type', e.target.value)}>
                {ITYPES.map(it => <option key={it} value={it}>{t(`itype_${it}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="row g-0">
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_date')}</label>
              <input type="date" className="form-control" value={form.invoice_date}
                onChange={e => set('invoice_date', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_period_start')}</label>
              <input type="date" className="form-control" value={form.period_start}
                onChange={e => set('period_start', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label small fw-medium">{t('inv_period_end')}</label>
              <input type="date" className="form-control" value={form.period_end}
                onChange={e => set('period_end', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-white fw-medium" style={{ fontSize: 14 }}>{t('inv_consumption')}</div>
        <div className="card-body">
          <div className="row g-0">
            {isElec ? (
              <>
                {numField('consumption_total', t('f_consumption_total'))}
                {numField('consumption_day', t('f_consumption_day'))}
                {numField('consumption_night', t('f_consumption_night'))}
                {numField('peak_power_kw', t('f_peak_power'))}
                {selectedConn?.has_solar && numField('injection_kwh', t('f_injection'))}
              </>
            ) : (
              <>
                {numField('consumption_total', t('f_gas_m3'))}
                {numField('consumption_kwh', t('f_gas_kwh'))}
                {numField('conversion_factor', t('f_factor'))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-white fw-medium" style={{ fontSize: 14 }}>{t('inv_amount')}</div>
        <div className="card-body">
          <div className="row g-0">
            {numField('amount_energy', t('f_amount_energy'))}
            {numField('amount_network', t('f_amount_network'))}
            {numField('amount_taxes', t('f_amount_taxes'))}
            {numField('amount_excl_vat', t('f_amount_excl'))}
            {numField('vat_amount', t('f_vat'))}
            {numField('amount_incl_vat', t('f_amount_incl'))}
          </div>
          <div className="mb-1">
            <label className="form-label small fw-medium">{t('f_notes')}</label>
            <textarea className="form-control" rows="2" value={form.notes}
              onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/invoices')}>
          {t('cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || !form.connection_id}>
          {loading && <span className="spinner-border spinner-border-sm me-2" />}
          {t('save_invoice')}
        </button>
      </div>
    </form>
  );
}
